import { useState, MouseEvent, ChangeEvent, KeyboardEvent, useRef, useEffect } from "react"
import { styled } from "@stitches/react"
import autosize from "autosize"

interface IProps {
  value: string
  editable?: boolean
  onChange?: (value?: string) => void
  onEditingChange?: (editing: boolean) => void
}

export default function EditableText(props: IProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(props.value)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    autosize(textAreaRef.current)
  }, [value, props.value])

  const onSetEditing = (newValue: boolean) => {
    if (newValue !== editing) {
      props.onEditingChange?.(newValue)
      setEditing(newValue)
    }
  }

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)

  const onBlur = () => {
    setValue(props.value)
    onSetEditing(false)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const shouldCommitChanges = isSubmitEnter(e)
    if (shouldCommitChanges || isEscape(e)) {
      if (shouldCommitChanges) props.onChange?.(value)
      setValue(props.value)
      onSetEditing(false)
      textAreaRef.current?.blur()
    }
  }

  const onDoubleClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault()
    if (props.editable) {
      setValue(props.value)
      onSetEditing(true)
      textAreaRef.current?.select()
    }
  }

  return (
    <>
      <StyledTextarea
        autoFocus
        editable={editing}
        ref={textAreaRef}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      {!editing && <DoubleClickOverlay onDoubleClick={onDoubleClick} />}
    </>
  )
}

EditableText.defaultProps = { editable: true }

const StyledTextarea = styled("textarea", {
  // doing this instead of {all: "unset"}
  // because for whatever reason it also
  // removes the text selection background
  // on safari  ¯\_(ツ)_/¯
  fontSize: "unset",
  fontFamily: "unset",
  backgroundColor: "unset",
  outline: "unset",
  border: "unset",
  textAlign: "unset",
  resize: "unset",
  width: "100%",
  variants: {
    editable: {
      true: { pointerEvents: "auto" },
      false: { pointerEvents: "none" }
    }
  }
})

const DoubleClickOverlay = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
})

function isSubmitEnter(e: KeyboardEvent): boolean {
  return e.key === "Enter" && (e.metaKey || e.ctrlKey)
}

function isEscape(e: KeyboardEvent): boolean {
  return e.key === "Escape" && !e.metaKey && !e.shiftKey && !e.ctrlKey
}
